import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

// Re-export for backward compatibility (many files import SharedApiError from here)
export { SharedApiError } from "./shared";

export const linkPreviewBodySchema = z.object({
  url: z.string().url(),
});

export const linkPreviewSuccessSchema = z.object({
  url: z.string().url(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  image_url: z.string().nullable(),
  favicon_url: z.string().nullable(),
  site_name: z.string().nullable(),
});

export type LinkPreviewBody = z.infer<typeof linkPreviewBodySchema>;
export type LinkPreviewSuccess = z.infer<typeof linkPreviewSuccessSchema>;

export function linkPreviewPath(): string {
  return "/api/link-preview";
}

export async function fetchLinkPreviewRequest({
  apiBaseUrl,
  accessToken,
  url,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  url: string;
  fetchImpl?: FetchLike;
}): Promise<LinkPreviewSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = linkPreviewBodySchema.parse({ url });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${linkPreviewPath()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to fetch link preview");
  }

  const payload = await response.json().catch(() => null);
  const parsed = linkPreviewSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid link preview response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
